import * as React from "react";
import { useNavigate } from "@reach/router";

import { deref } from "kanopi-components/utils/primitives";
import { useGlobal } from "kanopi-components/useGlobal";

import api, { $ } from "kw/api";

import { substituteRiskData } from "../util/substituteRiskData";
import { buildHistory } from "./buildHistory";
import {
  enquiryFields,
  questionFields,
  questionSetFields,
  riskProfileFields,
} from "kw/api/fragments";
import { GraphQLTypes } from "kw/api/zeus";

type TArgs = {
  prefix?: string;
  finishedRoute?: string;
  globalDataKey: string;
  onComplete?: TOnFormEventFunc;
  skipNext?: boolean;
};

type TReturn = [
  answerEnquiry: (questionFlowId: string, answers: object) => Promise<unknown>,
  data: {
    enquiry?: GraphQLTypes["EnquiryType"];
    riskProfile?: TRiskProfile;
    messages?: GraphQLTypes["MessageType"][];
  },
  loadingState: {
    loading: boolean;
  }
];

export function useAnswerEnquiry({
  prefix,
  finishedRoute,
  globalDataKey,
  onComplete,
  skipNext = false,
}: TArgs): TReturn {
  const navigate = useNavigate();
  const [data = {}, setData] = useGlobal(globalDataKey);
  const [loading, setLoading] = React.useState(false);
  async function answerEnquiry(questionFlowId: string, answers: object) {
    setLoading(true);
    try {
      const response = await api.mutation(getAnswerEnquiryQuery(), {
        input: {
          questionFlowId,
          answers: answers ? JSON.stringify(answers) : answers,
          skipNext: skipNext || false,
        },
      });
      const [enquiry] = substituteRiskData(
        response?.answerEnquiry?.currentEnquiry || {},
        response?.answerEnquiry?.riskProfile
      );
      enquiry.currentSet = response.answerEnquiry.currentSet;
      enquiry.maxSets = response.answerEnquiry.maxSets;
      buildHistory(enquiry.riskProfile, deref(response, "answerEnquiry.history"));
      setData({
        enquiry,
        riskProfile: enquiry.riskProfile,
        messages: response?.answerEnquiry?.messages,
      });
      if (!response?.answerEnquiry?.currentEnquiry) {
        await onComplete?.();
        if (finishedRoute) {
          navigate(finishedRoute);
        }
      } else if (prefix) {
        navigate(`${prefix}/${enquiry.questionSet.key}`);
      }
      return enquiry;
    } catch (e) {
      const validationErrors = e?.response?.errors?.find(
        ({ code }) => code === "question-validation"
      )?.details;
      if (validationErrors) {
        e.errors = validationErrors;
      }
      throw e;
    } finally {
      setLoading(false);
    }
  }
  return [answerEnquiry, data, { loading }];
}

function getAnswerEnquiryQuery() {
  return {
    answerEnquiry: [
      {
        input: $("input", "AnswerEnquiryInput!"),
      },
      {
        currentEnquiry: {
          ...enquiryFields,
          questionSet: {
            ...questionSetFields,
            questions: {
              ...questionFields,
            },
          },
          // latestAnswers: {
          //   ...answerFields,
          //   source: {
          //     ...dataProviderFields,
          //   },
          // },
        },
        "... on QuoteQuestionFlowType": {
          riskProfile: riskProfileFields,
        },
        "... on CoverQuestionFlowType": {
          riskProfile: riskProfileFields,
        },
        "... on CoverSummaryQuestionFlowType": {
          riskProfile: riskProfileFields,
        },
        "... on QuoteSummaryQuestionFlowType": {
          riskProfile: riskProfileFields,
        },
        "... on KnockoutQuestionFlowType": {
          riskProfile: riskProfileFields,
        },
        "... on LegalQuestionFlowType": {
          riskProfile: riskProfileFields,
        },
        "... on PolicyDetailsQuestionFlowType": {
          riskProfile: riskProfileFields,
        },
        "... on PolicyHolderQuestionFlowType": {
          riskProfile: riskProfileFields,
        },
        history: {
          questionSet: {
            id: true,
            key: true,
            label: true,
          },
        },
        maxSets: true,
        currentSet: true,
        messages: {
          kind: true,
          content: true,
        },
      },
    ],
  };
}
